import { z } from "zod";
import { CurrencySchema, DecimalSchema } from "../branded";
import {
  AllocationSliceSchema,
  CashflowEventSchema,
  DecompositionPointSchema,
  HoldingSchema,
  PortfolioSummarySchema,
  TimeseriesPointSchema,
} from "../entities";
import { PortfolioPeriodSchema } from "../enums";
import { EpochMsSchema } from "../primitives";

/** Optional account selector shared by portfolio reads (defaults to primary). */
export const PortfolioAccountQuerySchema = z.object({
  accountSeq: z.string().optional(),
});
export type PortfolioAccountQuery = z.infer<typeof PortfolioAccountQuerySchema>;

/** `GET /api/portfolio/summary`. */
export const PortfolioSummaryResponseSchema = PortfolioSummarySchema;
export type PortfolioSummaryResponse = z.infer<typeof PortfolioSummaryResponseSchema>;

/** `GET /api/portfolio/timeseries?period`. */
export const PortfolioTimeseriesQuerySchema = z.object({
  accountSeq: z.string().optional(),
  period: PortfolioPeriodSchema.default("90d"),
});
export type PortfolioTimeseriesQuery = z.infer<typeof PortfolioTimeseriesQuerySchema>;

export const PortfolioTimeseriesResponseSchema = z.object({
  period: PortfolioPeriodSchema,
  baseCurrency: CurrencySchema,
  points: z.array(TimeseriesPointSchema),
});
export type PortfolioTimeseriesResponse = z.infer<typeof PortfolioTimeseriesResponseSchema>;

/** `GET /api/portfolio/decomposition?from&to`. */
export const PortfolioDecompositionQuerySchema = z.object({
  accountSeq: z.string().optional(),
  from: z.coerce.number().int(),
  to: z.coerce.number().int(),
});
export type PortfolioDecompositionQuery = z.infer<typeof PortfolioDecompositionQuerySchema>;

export const PortfolioDecompositionResponseSchema = z.object({
  from: EpochMsSchema,
  to: EpochMsSchema,
  baseCurrency: CurrencySchema,
  startValue: DecimalSchema,
  endValue: DecimalSchema,
  externalFlow: DecimalSchema,
  marketPnl: DecimalSchema,
  twr: z.number(),
  points: z.array(DecompositionPointSchema),
});
export type PortfolioDecompositionResponse = z.infer<typeof PortfolioDecompositionResponseSchema>;

/** `GET /api/portfolio/allocation`. */
export const PortfolioAllocationResponseSchema = z.object({
  baseCurrency: CurrencySchema,
  stocksValue: DecimalSchema,
  cashValue: DecimalSchema,
  bySymbol: z.array(AllocationSliceSchema),
});
export type PortfolioAllocationResponse = z.infer<typeof PortfolioAllocationResponseSchema>;

/** `POST /api/portfolio/snapshot` — enqueue an on-demand snapshot for every connected account. */
export const SnapshotNowResponseSchema = z.object({ enqueued: z.number().int().min(0) });
export type SnapshotNowResponse = z.infer<typeof SnapshotNowResponseSchema>;

/** `GET /api/portfolio/holdings`. */
export const PortfolioHoldingsResponseSchema = z.object({
  accountSeq: z.string(),
  baseCurrency: CurrencySchema,
  holdings: z.array(HoldingSchema),
});
export type PortfolioHoldingsResponse = z.infer<typeof PortfolioHoldingsResponseSchema>;

/** `GET /api/portfolio/cashflows`. */
export const ListCashflowsResponseSchema = z.object({
  cashflows: z.array(CashflowEventSchema),
});
export type ListCashflowsResponse = z.infer<typeof ListCashflowsResponseSchema>;
